<script setup lang="ts">
/**
 * Шаблон «Крупная витрина» — широкое фото, крупный заголовок, кнопка во всю ширину.
 * Для коротких категорий и хитов: на 5 позиций плитка из трёх колонок смотрится пусто,
 * а две крупные карточки — как витрина.
 */
import { PRODUCT_CARD_PROP_DEFAULTS, type ProductCardEmits, type ProductCardProps } from './cardContract';
import { getCardDescription } from '@entities/product';

const props = withDefaults(defineProps<ProductCardProps>(), PRODUCT_CARD_PROP_DEFAULTS);
const emit = defineEmits<ProductCardEmits>();

const description = computed(() => getCardDescription(props.item));
/** В крупной карточке кнопка-иконка теряется — подпись включаем всегда. */
const actionVariant = computed(() => (props.config.actionStyle === 'icon' ? 'icon-text' : props.config.actionStyle));
</script>

<template>
  <div
    class="pcard pcard--showcase"
    :class="[`pcard--surface-${config.surface}`]"
    :data-testid="`product-card-${item.id}`"
    @click="emit('open')"
  >
    <ProductBadges
      v-if="config.showBadges"
      :tags="item.tags"
      :teg="item.teg"
      size="sm"
      :max-visible="4"
      class-name="pcard__badges"
    />

    <ProductCardFavorite
      v-if="favoriteEnabled"
      class="pcard__favorite"
      :active="!!item.is_favorite"
      :animation="favoriteAnimation"
      @click="emit('favorite')"
    />

    <ProductCardMedia :item="item" :aspect="config.imageAspect" :placeholder="placeholder" :priority="priority" />

    <div class="pcard__body">
      <h3 class="pcard__title">
        <!-- Ссылка, а не текст: карточка открывается модалкой, и без href у блюда
             не было ни одного адреса, по которому его мог бы найти поиск. Клик
             гасим — поведение витрины остаётся прежним, но Ctrl+клик и
             «скопировать адрес ссылки» теперь работают. -->
        <a v-if="href" :href="href" class="pcard__title-link" @click.prevent="emit('open')">{{ item.name }}</a>
        <template v-else>{{ item.name }}</template>
      </h3>
      <p v-if="config.showDescription && description" class="pcard__description">{{ description }}</p>
    </div>

    <div class="pcard__bottom">
      <ProductCardPrice class="pcard__price" :item="item" :variant="config.priceStyle" />
      <ProductCardAction
        block
        :in-cart="inCart"
        :cart-quantity="cartQuantity"
        :variant="actionVariant"
        :data-testid="inCart ? `product-card-incart-${item.id}` : `product-card-add-${item.id}`"
        @click="emit('action')"
      />
    </div>
  </div>
</template>
